import React, { useEffect, useState } from 'react';
import ApiService from '../../service/ApiService';
import { apiPaths } from '../../service/apiPaths';
import SidebarImg from '../../images/SideBar/Sidebar';
import toast from 'react-hot-toast';
import JibbleModal from './JibbleModal';
import { useStateContext } from '../../hooks/context/Context';
import DropdownUser from './DropdownUser';
import { AddButton } from '../Button';
import useTaskSheet from '../../role/Admin/Tasksheet/useTasksheet';

interface INavButtonProps {
  title: string;
  customFunc: () => void;
  icon: React.ReactNode;
  color: string;
  dotColor: string;
}

const NavButton: React.FC<INavButtonProps> = ({ title, customFunc, icon, color, dotColor }) => (
  <button type="button" onClick={() => customFunc()} style={{ color }} className="relative text-xl rounded-full hover:bg-light-gray">
    <span style={{ background: dotColor }} className="absolute inline-flex rounded-full h-2 w-2 right-2 top-2" />
    {icon}
  </button>
);

interface NavbarProps {
  isLoggedIn: boolean;
  onSidebarToggle: () => void;
}

export const Header: React.FC<NavbarProps> = ({ isLoggedIn, onSidebarToggle }) => {

  const {handleAdd} =useTaskSheet()
  const {
    currentColor,
    setActiveMenu,
    setScreenSize,
    screenSize,
  } = useStateContext();
  const [isJibbleLinked, setIsJibbleLinked] = useState<boolean>(false);
  const [isJibbleModalOpen, setIsJibbleModalOpen] = useState<boolean>(false);
  const [jibbleButtonText, setJibbleButtonText] = useState<string>('Jibble');
  const [elapsedTime, setElapsedTime] = useState<string>('');
  const [startTimestamp, setStartTimestamp] = useState<number | null>(null);
  const [jibbleNote, setJibbleNote] = useState<string>('');
  const [baseWorkedMs, setBaseWorkedMs] = useState<number | null>(null);
  const [baseWorkedFetchedAt, setBaseWorkedFetchedAt] = useState<number | null>(null);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (screenSize && screenSize <= 1101) {
      setActiveMenu(false);
    } else {
      setActiveMenu(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenSize]);

  useEffect(() => {
    const linked = localStorage.getItem('jibbleLinked');
    setIsJibbleLinked(linked === 'true');
    if (linked !== 'true') {
      const email = (localStorage.getItem('userEmail') || localStorage.getItem('loginUsername') || '').trim();
      if (email) {
        ApiService({
          method: 'POST',
          endpoint: apiPaths.jibbleLink,
          data: { email },
        })
          .then(() => {
            localStorage.setItem('jibbleLinked', 'true');
            setIsJibbleLinked(true);
          })
          .catch(() => {
            localStorage.removeItem('jibbleLinked');
            setIsJibbleLinked(false);
          });
      }
    }
    const saved = localStorage.getItem('jibbleStatusSelection');
    if (saved === 'IN' || saved === 'OUT') {
      setJibbleButtonText(saved);
      const savedNote = localStorage.getItem('jibbleNote') || '';
      setJibbleNote(savedNote);
    } else {
      setJibbleButtonText('Jibble');
    }
  }, []);

  // Listen for Jibble status changes from other components
  useEffect(() => {
    const handleJibbleStatusChange = (event: CustomEvent) => {
      const { status, note } = event.detail;
      setJibbleButtonText(status);
      if (note && note.trim()) {
        setJibbleNote(note.trim());
      } else {
        setJibbleNote('');
      }
      if (status === 'OUT') {
        setStartTimestamp(null);
        setElapsedTime('');
      }
      // Refresh Jibble status from server
      refreshJibbleStatus();
    };

    window.addEventListener('jibbleStatusChanged', handleJibbleStatusChange as EventListener);
    return () => {
      window.removeEventListener('jibbleStatusChanged', handleJibbleStatusChange as EventListener);
    };
  }, []);

  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600)
      .toString()
      .padStart(2, '0');
    const minutes = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor(totalSeconds % 60)
      .toString()
      .padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const parseIsoDurationToMs = (iso: string | null | undefined): number => {
    if (!iso || typeof iso !== 'string') return 0;
    // Supports patterns like PT4H33M57.4998315S or PT0S
    const re = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/;
    const m = iso.match(re);
    if (!m) return 0;
    const h = m[1] ? parseInt(m[1], 10) : 0;
    const min = m[2] ? parseInt(m[2], 10) : 0;
    const s = m[3] ? parseFloat(m[3]) : 0;
    return (((h * 60 + min) * 60) + s) * 1000;
  };

  const getTodayDateParam = (): string => {
    const d = new Date();
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const refreshJibbleStatus = async () => {
    try {
      const res = await ApiService({
        method: 'GET',
        endpoint: apiPaths.getJibbleStatus,
      });
      if (res) {
        const rawType: string = (res.type || res?.status?.name || '').toString();
        const normalized = rawType.trim().toUpperCase();
        const newStatus: 'IN' | 'OUT' = normalized.includes('IN') ? 'IN' : 'OUT';
        const previousStatus = localStorage.getItem('jibbleStatusSelection') as 'IN' | 'OUT' | null;
        
        // Only update if status actually changed
        if (previousStatus !== newStatus) {
          localStorage.setItem('jibbleStatusSelection', newStatus);
          setJibbleButtonText(newStatus);
          
          if (newStatus === 'IN') {
            // If status changed to IN, set note to "Working" and start timer
            setJibbleNote('Working');
            localStorage.setItem('jibbleNote', 'Working');
            fetchJibbleStartTime();
          } else {
            // If status changed to OUT, clear timer and note
            setStartTimestamp(null);
            setElapsedTime('');
            setJibbleNote('');
            localStorage.removeItem('jibbleNote');
          }
        }
      }
    } catch {
      // ignore; non-blocking sync
    }
  };

  const fetchJibbleStartTime = async () => {
    try {
      const date = getTodayDateParam();
      const res = await ApiService({
        method: 'GET',
        endpoint: `${apiPaths.jibbleTimesheet}?date=${date}`,
      });
      const items = Array.isArray(res) ? res : [res];
      const today = items.find((it: any) => it && it.date === date);
      if (today) {
        // Update worked time from API
        const workedMs = parseIsoDurationToMs(res[0].trackedHours.total);
        setBaseWorkedMs(workedMs);
        setBaseWorkedFetchedAt(Date.now());
        setElapsedTime(formatDuration(workedMs));

        // If IN (no lastOutTimestamp), remember start for live tick
        if (today.firstInTimestamp && !today.lastOutTimestamp) {
          const ts = Date.parse(today.firstInTimestamp);
          if (!Number.isNaN(ts)) {
            setStartTimestamp(ts);
          }
        } else {
          setStartTimestamp(null);
        }
      }
    } catch (e) {
      // ignore fetch errors for timer UI
    }
  };

  // Check Jibble status on app start and start timer if IN
  useEffect(() => {
    const checkInitialJibbleStatus = async () => {
      // Only check if user is logged in
      const token = localStorage.getItem("accessToken");
      if (!token) return;

      // Check if Jibble is linked (either from state or localStorage)
      const jibbleLinked = isJibbleLinked || localStorage.getItem('jibbleLinked') === 'true';
      if (!jibbleLinked) return;

      try {
        const res = await ApiService({
          method: 'GET',
          endpoint: apiPaths.getJibbleStatus,
        });
        
        if (res) {
          const rawType: string = (res.type || res?.status?.name || '').toString();
          const normalized = rawType.trim().toUpperCase();
          const status: 'IN' | 'OUT' = normalized.includes('IN') ? 'IN' : 'OUT';
          
          if (status === 'IN') {
            // Set status to IN
            setJibbleButtonText('IN');
            localStorage.setItem('jibbleStatusSelection', 'IN');
            
            // Set note to "Working"
            setJibbleNote('Working');
            localStorage.setItem('jibbleNote', 'Working');
            
            // Start timer by fetching timesheet
            fetchJibbleStartTime();
          } else {
            // If OUT, just update the status
            setJibbleButtonText('OUT');
            localStorage.setItem('jibbleStatusSelection', 'OUT');
          }
        }
      } catch (error) {
        // Silently fail - non-blocking
        console.warn('Failed to check initial Jibble status:', error);
      }
    };

    // Run check after a short delay to ensure component is fully initialized
    const timeoutId = setTimeout(() => {
      checkInitialJibbleStatus();
    }, 1000);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Always fetch today's timesheet to get worked time from server
    fetchJibbleStartTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jibbleButtonText]);

  useEffect(() => {
    // When IN, tick forward from the last fetched worked value
    if (jibbleButtonText !== 'IN' || baseWorkedMs == null || baseWorkedFetchedAt == null) return;
    const id = window.setInterval(() => {
      const delta = Date.now() - baseWorkedFetchedAt;
      setElapsedTime(formatDuration(baseWorkedMs + delta));
    }, 1000);
    // Update immediately
    const deltaNow = Date.now() - baseWorkedFetchedAt;
    setElapsedTime(formatDuration(baseWorkedMs + deltaNow));
    return () => window.clearInterval(id);
  }, [jibbleButtonText, baseWorkedMs, baseWorkedFetchedAt]);

  // Poll Jibble status periodically to sync with mobile app changes
  useEffect(() => {
    // Only poll if user is logged in and Jibble is linked
    const token = localStorage.getItem("accessToken");
    const jibbleLinked = isJibbleLinked || localStorage.getItem('jibbleLinked') === 'true';
    
    if (!token || !jibbleLinked) return;

    // Poll every 30 seconds to check for status changes from mobile
    const pollInterval = setInterval(() => {
      refreshJibbleStatus();
    }, 30000); // 30 seconds

    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJibbleLinked]);

  const handleJibbleAction = (status: 'IN' | 'OUT', activityId?: string, note?: string) => {
    ApiService({
      method: 'POST',
      endpoint: apiPaths.jibbleStatus,
      data: {
        status_type: status,
        ...(status === 'IN' && activityId ? { status_id: activityId } : {}),
        ...(note && note.trim() ? { note: note.trim() } : {}),
      },
    })
      .then(() => {
        localStorage.setItem('jibbleStatusSelection', status);
        setJibbleButtonText(status);
        if (note && note.trim()) {
          const noteToStore = note.trim();
          setJibbleNote(noteToStore);
          localStorage.setItem('jibbleNote', noteToStore);
        } else {
          setJibbleNote('');
          localStorage.removeItem('jibbleNote');
        }
        if (status === 'IN') {
          setStartTimestamp(Date.now());
          // Refresh worked from server but show live immediately
          fetchJibbleStartTime();
        } else {
          setStartTimestamp(null);
          setElapsedTime('');
          // Fetch final worked time from server after OUT
          fetchJibbleStartTime();
        }
        // Also fetch and sync latest jibble status from server
        refreshJibbleStatus();
        toast.success(`Jibble ${status} recorded`);
        setIsJibbleModalOpen(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Failed to update Jibble status');
      });
  };

  return (
    <header className={`sticky ml-2 top-0 z-999 flex max-w-full bg-white shadow-lg`}>
      <div className="flex flex-grow items-center justify-between px-4 py-[14px] border border-border2 md:px-6 2xl:px-11">
        <div className="flex justify-start rounded-full -ml-[1%] relative mt-2">
          <NavButton title="Menu" customFunc={onSidebarToggle} color={currentColor} icon={<SidebarImg />} dotColor="" />
        </div>
        <div className="flex items-center gap-3 2xsm:gap-4">
            <div className="grid justify-items-end ">
                  <AddButton
                    onClick={handleAdd}
                    className=""
                    children="+"
                    type="button"
                  />
                </div>
          <ul className="flex items-center gap-2 2xsm:gap-4">
            {/* <DropdownNotification />
            <DropdownMessage /> */}
          </ul>
          {isJibbleLinked ? (
            <>
              <div className="ml-2 flex flex-col leading-tight">
                {elapsedTime ? (
                  <span className="text-xs font-mono text-fontColor">{elapsedTime}</span>
                ) : null}
                {jibbleNote ? (
                  <span className="text-[10px] text-fontColor/80">{jibbleNote}</span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setIsJibbleModalOpen(true)}
                className={`px-3 py-1 rounded-md text-white text-sm ${
                  jibbleButtonText === 'IN' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {jibbleButtonText === 'IN' ? 'Day off' : 'Clock in'}
              </button>
              
            </>
          ) : (
            <button
              type="button"
              onClick={() => window.open('https://app.jibble.io', '_blank')}
              className="px-3 py-1 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
            >
              Link Jibble
            </button>
          )}
          <DropdownUser />
        </div>
      </div>
      <JibbleModal
        open={isJibbleModalOpen}
        onClose={() => setIsJibbleModalOpen(false)}
        onSubmit={handleJibbleAction}
      />
    </header>
  );
};
